import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs/operators';

declare global {
    interface Window {
        _satellite?: any;
        analyticsDataLayer?: any;
        adobeLaunchReady?: boolean;
        getCurrentUser?: () => string;
        __env?: any;
    }
}

export enum InteractionType {
    BUTTON_CLICK = 'button click',
    NAVIGATION_LINK = 'navigation link',
    LINKS = 'links',
    PAGINATION = 'pagination',
    TAB_CLICK = 'tab click',
    MENU_CLICK = 'menu click',
    MODAL_OPEN = 'modal open',
    MODAL_CLOSE = 'modal close',
    DOWNLOAD = 'Download',
    SEARCH = 'Search',
    FILTER = 'Filter',
    SORT = 'Sort',
    FORM_SUBMIT = 'form submit',
    CUSTOM = 'custom'
}

export interface WebInteractionData {
    action: string;
    eventType: InteractionType | string;
}

export interface DownloadData {
    downloadeventType: 'Download';
    downloadName: string;
    downloadType: string;
}

export interface SearchData {
    searchEventType: 'Search';
    searchTerm: string;
}

export interface SortData {
    sortEventType: 'Sort';
    sortField: string;
    sortOrder: string;
}

export interface FilterData {
    filtereventType: 'Filter';
    filterName: string;
    filterValue: string;
}

export interface TrackingOptions {
    webInteraction?: WebInteractionData;
    download?: DownloadData;
    search?: SearchData;
    sort?: SortData;
    filter?: FilterData;
    customData?: any;
}

/**
 * DBadge user-alias storage key shared with the inline bootstrap script in index.html
 * (see window.getCurrentUser()).
 */
const USER_ALIAS_STORAGE_KEY = 'dbadgeUserAlias';

/**
 * Adobe Experience Platform / Customer Journey Analytics (AEP CJA) tracking service.
 *
 * Adobe Launch (the tag manager, `window._satellite`) is the delivery mechanism that forwards
 * the `analyticsDataLayer` data to the AEP Web SDK / Analytics extension, which in turn feeds
 * Customer Journey Analytics. This service owns the data layer + tracking calls; the actual
 * Launch library is bootstrapped from `index.html` (see the inline script there) using the
 * per-environment URL configured in `assets/config.js` (`window.__env.analyticsSourceUrl`).
 */
@Injectable({
    providedIn: 'root'
})
export class AdobeLaunchService {
    /**
     * Static, constant application name reported to AEP CJA for every event. This intentionally
     * does NOT change with the page title (unlike the browser tab title, which updates per route).
     */
    private readonly applicationName = 'DBadge';
    private isReady = false;
    private lastTrackedPage: string | null = null;
    private lastTrackedButton: string | null = null;
    private lastPageViewTime = 0;
    private lastInteractionTime = 0;
    private readonly deduplicationWindow = 500;

    constructor(
        private router: Router,
        private titleService: Title,
        private activatedRoute: ActivatedRoute,
    ) {
        this.initialize();
    }

    private initialize(): void {
        if (typeof window === 'undefined') {
            return;
        }

        if (window.adobeLaunchReady) {
            this.isReady = true;
            this.setupAutoTracking();
        } else {
            window.addEventListener('adobeLaunchReady', () => {
                this.isReady = true;
                this.setupAutoTracking();
            });
        }
    }

    private setupAutoTracking(): void {
        setTimeout(() => {
            this.trackPageView(this.getPageName());
        }, 300);

        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe(() => {
                setTimeout(() => {
                    this.trackPageView(this.getPageName());
                }, 300);
            });

        this.setupGlobalClickTracking();
    }

    /**
     * Resolves the page name for analytics as just the top-level route segment - no origin/host,
     * no leading/trailing slashes, no query string, and no nested child segments/IDs.
     * e.g. "http://localhost:4200/photo-review?categoryId=1" -> "photo-review"
     *      "http://localhost:4200/employee-profile/123" -> "employee-profile"
     * Still changes on every navigation since it re-reads window.location on each call.
     */
    private getPageName(): string {
        const segments = (window.location.pathname || '/').split('/').filter(Boolean);
        return segments[0] || '/';
    }

    /**
     * Observes clicks passively (bubble phase, no preventDefault/stopPropagation/replay) so that
     * native click behaviour - window.open() popups, ctrl/cmd/middle-click "open in new tab",
     * downloads, and other components' own click/document:click handlers - is never altered or
     * delayed by tracking. This intentionally deviates from the guide's capture+replay pattern,
     * which was found to silently break window.open() popups and modifier-key link clicks.
     */
    private setupGlobalClickTracking(): void {
        if (typeof document === 'undefined') {
            return;
        }

        document.addEventListener('click', (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            const trackableElement = target.closest(
                '[data-track-click], button, a[href], [role="button"]'
            );

            if (!trackableElement) {
                return;
            }

            const element = trackableElement as HTMLElement;
            const trackingName = this.getTrackingName(element);
            if (!trackingName) {
                return;
            }

            const interactionType = this.determineInteractionType(element);
            const downloadData = this.getDownloadData(element);
            const searchData = this.getSearchData(element);
            const sortData = this.getSortData(element);
            const filterData = this.getFilterData(element);

            this.trackInteraction(trackingName, {
                webInteraction: downloadData || searchData || sortData || filterData ? undefined : {
                    action: trackingName,
                    eventType: interactionType
                },
                download: downloadData,
                search: searchData,
                sort: sortData,
                filter: filterData
            });
        });
    }

    private determineInteractionType(element: HTMLElement): InteractionType | string {
        const explicitType = element.getAttribute('data-interaction-type');
        if (explicitType) {
            return explicitType;
        }

        if (element.hasAttribute('download') || element.classList.contains('download-btn')) {
            return InteractionType.DOWNLOAD;
        }

        if (element.getAttribute('type') === 'submit' && element.closest('form')?.querySelector('input[type="search"]')) {
            return InteractionType.SEARCH;
        }

        if (element.tagName === 'A' && element.getAttribute('href')) {
            const href = element.getAttribute('href') || '';
            return href.startsWith('#') ? InteractionType.TAB_CLICK : InteractionType.NAVIGATION_LINK;
        }

        if (element.classList.contains('sort-btn') || element.getAttribute('data-sort')) {
            return InteractionType.SORT;
        }

        if (element.classList.contains('filter-btn') || element.getAttribute('data-filter')) {
            return InteractionType.FILTER;
        }

        if (element.classList.contains('pagination') || element.closest('.pagination')) {
            return InteractionType.PAGINATION;
        }

        if (element.getAttribute('type') === 'submit') {
            return InteractionType.FORM_SUBMIT;
        }

        if (element.getAttribute('data-bs-toggle') === 'modal') {
            return InteractionType.MODAL_OPEN;
        }

        if (element.classList.contains('modal-close')) {
            return InteractionType.MODAL_CLOSE;
        }

        if (element.classList.contains('menu-item') || element.closest('.menu')) {
            return InteractionType.MENU_CLICK;
        }

        return InteractionType.BUTTON_CLICK;
    }

    private getDownloadData(element: HTMLElement): DownloadData | undefined {
        const downloadName = element.getAttribute('data-download-name') || element.getAttribute('download');
        if (!downloadName) {
            return undefined;
        }

        const downloadType = element.getAttribute('data-download-type') ||
            this.getFileExtension(downloadName) || 'unknown';

        return {
            downloadeventType: 'Download',
            downloadName,
            downloadType: downloadType.toUpperCase()
        };
    }

    private getSearchData(element: HTMLElement): SearchData | undefined {
        const searchTerm = element.getAttribute('data-search-term');
        if (searchTerm) {
            return { searchEventType: 'Search', searchTerm };
        }

        const container = element.closest('form, .header-search, .search-container, [class*="search"]');
        if (container) {
            const searchInput = container.querySelector('input[type="search"], input[name="search"], .search-input') as HTMLInputElement;
            if (searchInput?.value?.trim()) {
                return { searchEventType: 'Search', searchTerm: searchInput.value.trim() };
            }
        }
        return undefined;
    }

    private getSortData(element: HTMLElement): SortData | undefined {
        const sortField = element.getAttribute('data-sort');
        if (!sortField) {
            return undefined;
        }

        const sortOrder = element.getAttribute('data-sort-order') || 'asc';
        return { sortEventType: 'Sort', sortField, sortOrder };
    }

    private getFilterData(element: HTMLElement): FilterData | undefined {
        const filterName = element.getAttribute('data-filter');
        if (!filterName) {
            return undefined;
        }

        let filterValue = element.getAttribute('data-filter-value') || '';
        if (element.tagName === 'SELECT') {
            filterValue = (element as HTMLSelectElement).value;
        }
        return { filtereventType: 'Filter', filterName, filterValue };
    }

    private getFileExtension(filename: string): string | null {
        const match = filename.match(/\.([^.]+)$/);
        return match ? match[1] : null;
    }

    private getTrackingName(element: HTMLElement): string | null {
        const dataTrackClick = element.getAttribute('data-track-click');
        if (dataTrackClick) {
            return dataTrackClick;
        }

        if (element.hasAttribute('data-track-click-disabled')) {
            return null;
        }

        const ariaLabel = element.getAttribute('aria-label');
        if (ariaLabel?.trim()) {
            return ariaLabel.trim();
        }

        const text = element.textContent?.trim();
        if (text && text.length > 0 && text.length < 50) {
            return text;
        }

        if (element.tagName === 'A') {
            const href = element.getAttribute('href');
            if (href && href !== '#') {
                return `Navigate to ${href}`;
            }
        }

        return `${element.tagName} Click`;
    }

    private getCurrentUser(): string {
        if (typeof window !== 'undefined' && window.getCurrentUser) {
            return window.getCurrentUser();
        }
        return 'anonymous';
    }

    /**
     * Stores the current user's alias so the inline bootstrap script (window.getCurrentUser)
     * and this service can both report the same userID to AEP CJA via Adobe Launch.
     */
    public setUser(userAlias: string): void {
        if (typeof window === 'undefined' || !userAlias) {
            return;
        }
        try {
            sessionStorage.setItem(USER_ALIAS_STORAGE_KEY, userAlias);
        } catch {
            // ignore storage errors (e.g. private browsing restrictions)
        }
    }

    /**
     * Keeps the browser tab title in sync with route data, independently of tracking.
     */
    public trackPageTitleFromRouteData(): void {
        this.router.events
            .pipe(
                filter((event: unknown) => event instanceof NavigationEnd),
                map(() => this.activatedRoute),
                map((route) => {
                    while (route.firstChild) {
                        route = route.firstChild;
                    }
                    return route;
                }),
                filter((route) => route.outlet === 'primary'),
                mergeMap((route) => route.data),
                filter((data) => data.title)
            )
            .subscribe((data) => {
                this.titleService.setTitle(`DBadge-${data.title}`);
            });
    }

    public trackPageView(pageName: string): void {
        if (!this.isReady || typeof window === 'undefined') {
            return;
        }

        try {
            const fullPath = pageName || (window.location.pathname || '/');
            const now = Date.now();

            if (this.lastTrackedPage === fullPath && (now - this.lastPageViewTime) < this.deduplicationWindow) {
                return;
            }

            this.lastTrackedPage = fullPath;
            this.lastPageViewTime = now;

            window.analyticsDataLayer = {
                pageInfo: {
                    pageName: fullPath,
                    pageURL: window.location.href,
                    userID: this.getCurrentUser(),
                    applicationName: this.applicationName,
                    timestamp: new Date().toISOString()
                }
            };

            if (window._satellite && typeof window._satellite.track === 'function') {
                window._satellite.track('pageView');
            }
        } catch (error) {
            console.error('Error tracking page view:', error);
        }
    }

    public trackInteraction(buttonName: string, options?: TrackingOptions): void {
        if (!this.isReady || typeof window === 'undefined') {
            return;
        }

        try {
            const now = Date.now();
            // Include the actual tracked value (filter value, search term, sort order, download name)
            // in the key so that two DIFFERENT interactions on the same control (e.g. re-applying a
            // filter with a new value within the dedup window) are never mistaken for a duplicate click.
            const deduplicationKey = [
                buttonName,
                options?.webInteraction?.eventType,
                options?.filter?.filterValue,
                options?.search?.searchTerm,
                options?.sort?.sortOrder,
                options?.download?.downloadName
            ].filter(part => part !== undefined && part !== null).join('|');

            if (this.lastTrackedButton === deduplicationKey && (now - this.lastInteractionTime) < this.deduplicationWindow) {
                return;
            }

            this.lastTrackedButton = deduplicationKey;
            this.lastInteractionTime = now;

            const dataLayer: any = {
                pageInfo: {
                    pageName: this.getPageName(),
                    userID: this.getCurrentUser(),
                    applicationName: this.applicationName,
                    timestamp: new Date().toISOString()
                }
            };

            if (options?.webInteraction) {
                dataLayer.webInteraction = {
                    action: options.webInteraction.action,
                    eventType: options.webInteraction.eventType
                };
            }

            if (options?.download) {
                dataLayer.download = {
                    downloadeventType: options.download.downloadeventType,
                    downloadName: options.download.downloadName,
                    downloadType: options.download.downloadType
                };
            }

            if (options?.search) {
                dataLayer.search = {
                    searchEventType: options.search.searchEventType,
                    searchTerm: options.search.searchTerm
                };
            }

            if (options?.sort) {
                dataLayer.sort = {
                    sortEventType: options.sort.sortEventType,
                    sortField: options.sort.sortField,
                    sortOrder: options.sort.sortOrder
                };
            }

            if (options?.filter) {
                dataLayer.filter = {
                    filtereventType: options.filter.filtereventType,
                    filterName: options.filter.filterName,
                    filterValue: options.filter.filterValue
                };
            }

            if (options?.customData) {
                Object.assign(dataLayer, options.customData);
            }

            window.analyticsDataLayer = dataLayer;

            if (window._satellite && typeof window._satellite.track === 'function') {
                window._satellite.track('action');
            }
        } catch (error) {
            console.error('Error tracking interaction:', error);
        }
    }

    public trackDownload(downloadName: string, downloadType: string): void {
        this.trackInteraction(downloadName, {
            download: {
                downloadeventType: 'Download',
                downloadName,
                downloadType: downloadType.toUpperCase()
            }
        });
    }

    public trackSearch(searchTerm: string): void {
        this.trackInteraction('Search', {
            search: {
                searchEventType: 'Search',
                searchTerm
            }
        });
    }

    public trackSort(sortField: string, sortOrder: string = 'asc'): void {
        this.trackInteraction(`Sort by ${sortField}`, {
            sort: {
                sortEventType: 'Sort',
                sortField,
                sortOrder
            }
        });
    }

    public trackFilter(filterName: string, filterValue: string): void {
        this.trackInteraction(`Filter ${filterName}`, {
            filter: {
                filtereventType: 'Filter',
                filterName,
                filterValue
            }
        });
    }

    public isAdobeLaunchReady(): boolean {
        return this.isReady;
    }

    public getDataLayer(): any {
        return typeof window !== 'undefined' ? window.analyticsDataLayer : null;
    }
}
